/* Expense > Banks — connect a bank/card account (Mastercard Open Finance / Finicity) and
   auto-import card charges (Phase 3). "Connect a bank" opens the Finicity Connect flow in a
   new tab; once linked, "Import now" pulls new CARD_CHARGE lines onto the current OPEN report
   and auto-matches any texted/uploaded receipts. Entity + owner isolation is server-side. */
import {
  ArrowSquareOutIcon,
  BankIcon,
  DownloadSimpleIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@andavo/web-ui';

import {
  useBankConnections,
  useBankImport,
  useConnectUrl,
  useSyncConnections,
  type ImportResult,
} from './hooks/use-bank';

// Finicity Connect posts completion events to the opener. Only trust messages from a *.finicity.com
// origin (the hosted Connect host is connect2.finicity.com) — never act on a message from any other
// window, and never trust the message as authoritative for recording (the SERVER reconciles).
const FINICITY_ORIGIN_RE = /^https:\/\/[a-z0-9-]+\.finicity\.com$/;

type ConnectOutcome = 'done' | 'cancel' | 'error' | 'closed';

/**
 * Resolve when the Finicity Connect popup signals completion. Two independent signals — whichever
 * fires first wins:
 *   - a postMessage from the Connect origin ('done' | 'cancel' | 'error'), the happy path;
 *   - the popup being closed ('closed'), the fallback for a dropped/missed message so we never hang.
 * postMessage is only a UX hint here; the actual connection recording is server-side (sync).
 */
function waitForConnectCompletion(popup: Window): Promise<ConnectOutcome> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (r: ConnectOutcome) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMsg);
      clearInterval(poll);
      resolve(r);
    };
    const onMsg = (e: MessageEvent) => {
      if (!FINICITY_ORIGIN_RE.test(e.origin)) return;
      let data: unknown = e.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      const type =
        (data as { type?: string; event?: string })?.type ??
        (data as { event?: string })?.event;
      if (type === 'done' || type === 'added') finish('done');
      else if (type === 'cancel') finish('cancel');
      else if (type === 'error') finish('error');
    };
    window.addEventListener('message', onMsg);
    const poll = setInterval(() => {
      if (popup.closed) finish('closed');
    }, 500);
  });
}

export function BankScreen() {
  const { data: connections = [], isLoading, isError } = useBankConnections();
  const connectUrl = useConnectUrl();
  const syncConnections = useSyncConnections();
  const bankImport = useBankImport();
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);

  /**
   * Reconcile the caller's linked accounts server-side. Aggregation can lag the Connect 'done' event,
   * so on the happy path poll the idempotent sync on a bounded window until an ACTIVE account appears.
   */
  async function syncWithRetry(pollForAccounts: boolean) {
    const attempts = pollForAccounts ? 6 : 1;
    for (let i = 0; i < attempts; i++) {
      const rows = await syncConnections.mutateAsync();
      if (rows.some((r) => r.status === 'ACTIVE')) return;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 2000));
    }
  }

  async function onConnect() {
    setError(null);
    setImportResult(null);
    // Open the popup SYNCHRONOUSLY inside the click gesture — a window.open that runs after an
    // awaited fetch is treated as non-user-initiated and gets blocked. Navigate it once the URL lands.
    const popup = window.open('', 'finicity-connect', 'width=620,height=760');
    if (!popup) {
      setError(
        'Popup blocked — allow popups for this site, then click Connect a bank again.',
      );
      return;
    }
    let url: string;
    try {
      ({ url } = await connectUrl.mutateAsync());
    } catch {
      popup.close();
      setError(
        'Bank connect is not available yet. If this persists, the aggregator link needs to be finished.',
      );
      return;
    }
    popup.location.href = url;
    setLinking(true);
    try {
      const outcome = await waitForConnectCompletion(popup);
      if (!popup.closed) popup.close();
      if (outcome === 'error') {
        setError('The bank link didn’t complete. Please try again.');
      } else if (outcome !== 'cancel') {
        // 'done' (explicit success → poll for aggregation) or 'closed' (best-effort single sync).
        await syncWithRetry(outcome === 'done');
      }
    } finally {
      setLinking(false);
    }
  }

  async function onImport() {
    setError(null);
    setImportResult(null);
    try {
      setImportResult(await bankImport.mutateAsync());
    } catch {
      setError(
        'Import failed — the bank feed may not be fully configured yet.',
      );
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="font-sans font-semibold text-content-primary text-lg">
            Connected accounts
          </h2>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={onImport}
              disabled={bankImport.isPending || connections.length === 0}
              data-import-now
            >
              <DownloadSimpleIcon weight="bold" />
              {bankImport.isPending ? 'Importing…' : 'Import now'}
            </Button>
            <Button
              onClick={onConnect}
              disabled={connectUrl.isPending || linking}
              data-connect-bank
            >
              <BankIcon weight="bold" />
              {connectUrl.isPending
                ? 'Opening…'
                : linking
                  ? 'Linking…'
                  : 'Connect a bank'}
            </Button>
          </div>
        </div>

        <p className="text-content-secondary text-sm">
          Link a bank or card account and your card charges import
          automatically, matching to the receipts you text or upload. Powered by
          Mastercard Open Finance.
        </p>

        {error && (
          <Card className="border-status-danger-border bg-status-danger-subtle p-4 text-sm text-status-danger-content">
            {error}
          </Card>
        )}

        {importResult && (
          <Card
            className="border-status-success-border bg-status-success-subtle p-4 text-sm text-status-success-content"
            data-import-result
          >
            Imported {importResult.imported} charge
            {importResult.imported === 1 ? '' : 's'}
            {importResult.skippedDuplicate > 0 &&
              ` (${importResult.skippedDuplicate} already imported)`}
            , auto-matched {importResult.matched} receipt
            {importResult.matched === 1 ? '' : 's'}.
          </Card>
        )}
      </section>

      {isLoading ? (
        <p className="text-content-secondary text-sm">Loading connections…</p>
      ) : isError ? (
        <p className="text-status-danger-content text-sm">
          Couldn’t load bank connections.
        </p>
      ) : connections.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 p-8 text-center">
          <BankIcon size={32} className="text-content-tertiary" />
          <p className="font-sans font-medium text-content-primary">
            No banks connected yet
          </p>
          <p className="text-content-secondary text-sm">
            Connect a bank to import your card charges automatically.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Institution</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last synced</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {connections.map((c) => (
                <TableRow key={c.connectionId} data-connection-row>
                  <TableCell className="font-medium">
                    {c.institutionName ?? 'Bank'}
                  </TableCell>
                  <TableCell>••••{c.accountMask ?? '????'}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        c.status === 'ACTIVE'
                          ? 'success'
                          : c.status === 'ERROR'
                            ? 'danger'
                            : 'default'
                      }
                    >
                      {c.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-content-secondary">
                    {c.lastSyncedTxnDate ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <p className="flex items-center gap-1 text-content-tertiary text-xs">
        <ArrowSquareOutIcon />
        Connect opens Mastercard Open Finance in a new tab. Sandbox: link the
        “FinBank” test institution.
      </p>
    </div>
  );
}
